import classnames from 'classnames'
import {NullAsDefault} from "views/Shared";
import _ from 'lodash'
import React, {useCallback} from 'react'
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd'

const DraggableWrapper = ({
  children,
  onDragEnd = NullAsDefault,
  onDragStart = NullAsDefault,
  data = [],
  getDestinationValue = NullAsDefault,
  ...props
}) => {
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)

    return result
  }

  const handleAction = useCallback((result) => {
    if (!result.destination) {
      return
    }

    const { draggableId, destination, source } = result

    const fromIndex = source.index

    const toIndex = destination.index

    const sortedLocations = reorder(data, fromIndex, toIndex)

    onDragEnd({
      draggableId,
      items: sortedLocations,
      destination: getDestinationValue(toIndex),
    })
  }, [])

  return (
    <DragDropContext
      onBeforeCapture={NullAsDefault}
      onBeforeDragStart={NullAsDefault}
      onDragStart={onDragStart}
      onDragUpdate={NullAsDefault}
      onDragEnd={handleAction}>
      {children}
    </DragDropContext>
  )
}

const DraggableWidget = ({
  itemClassName,
  isDragDisabled = false,
  data = [],
  renderItem = NullAsDefault,
  onSelect = NullAsDefault,
  onCompleted = NullAsDefault,
  withClassNames = NullAsDefault,
  sortBy = 'id',
}) => {
  const getItemStyle = (isDragging, draggableStyle) => ({
    userSelect: 'none',
    ...draggableStyle,
  })

  const defaultClassName = []

  const bindingClassNames = (isDragging) => {
    const values = withClassNames(isDragging, defaultClassName)

    if (_.isArray(values)) {
      return values.map((e) => _.trim(e))
    }
    return defaultClassName
  }

  const locationIds = data.map((e) => e.id)

  const getDestinationValue = useCallback(
    (index) => {
      const currentLocation = data[index]
      return _.get(currentLocation, sortBy)
    },
    [sortBy, data]
  )

  return (
    <DraggableWrapper
      data={locationIds}
      getDestinationValue={getDestinationValue}
      onDragStart={({ draggableId }) => {
        onSelect(draggableId)
      }}
      onDragEnd={onCompleted}>
      <Droppable droppableId="droppable">
        {(provided, snapshot) => {
          const { isDraggingOver } = snapshot
          return (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}>
              {data.map((item, index) => {
                return (
                  <Draggable
                    isDragDisabled={isDragDisabled}
                    key={item.id}
                    draggableId={item.id}
                    index={index}>
                    {(
                      { innerRef, draggableProps, dragHandleProps },
                      snapshot
                    ) => {
                      const { isDragging } = snapshot
                      if (isDragging) {
                        draggableProps.style.left =
                          draggableProps.style.offsetLeft
                      }
                      return (
                        <div
                          ref={innerRef}
                          className={classnames(
                            itemClassName,
                            bindingClassNames(isDragging)
                          )}
                          style={getItemStyle(isDragging, draggableProps.style)}
                          {...draggableProps}
                          {...dragHandleProps}>
                          {renderItem(item, { isDragging })}
                        </div>
                      )
                    }}
                  </Draggable>
                )
              })}
            </div>
          )
        }}
      </Droppable>
    </DraggableWrapper>
  )
}

export default DraggableWidget
